

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {BoListPanel, IOnOffHandlers} from "bonomo/dist";
import {IShortCommunity} from "@/model/Community/communityInterfaces";
import {IShortServiceProvider} from "@/model/shortInterfaces";
import CommunityChip from "@/components/shared/chips/CommunityChip.vue";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import ServiceProviderSearch from "@/components/serviceProvider/widget/ServiceProviderSearch.vue";
import BoListFilters from "@/components/shared/BoListFilters.vue";
import SPNav from "@/components/serviceProvider/widget/SPNav.vue";
import {Prop} from "vue-property-decorator";
import {communityServiceProviderService} from "@/services/communityServiceProviderService";

const communityModule = namespace("communityModule");
  const serviceProviderModule = namespace("serviceProviderModule");

  interface ICommunityWithConnect {
    id: number;
    name: string;
    active: boolean;
    connectModel: boolean;
    location_city: string | null;
  }

  @Component({
    components: {SPNav, BoListFilters, BoListPanel, CommunityChip, ServiceProviderSearch }
  })
  export default class ProvidersCommunities extends Vue {

    @communityModule.Action('fetchAllCommunities')
    private fetchAllCommunities!: () => any;
    @communityModule.Getter('communityList')
    private communities!: IShortCommunity[];

    @serviceProviderModule.Action('fetchServiceProviders')
    protected fetchServiceProviders!: () => any;
    @serviceProviderModule.Getter('serviceProviders')
    private serviceProviders!: IShortServiceProvider[];

    @Prop()
    spId!: number;

    private communityItems: ICommunityWithConnect[] = [];
    private cityByCommunityId: Record<number, string> = {};
    private search: string = '';
    private onlyActive: boolean = true;
    private onlyAssigned: boolean = true;
    private selectedCities: string[] = [];
    private connectionsLoading: boolean = true;
    private subscribes: IOnOffHandlers = {};

    protected get assignedCnt(): number {
      return this.communityItems.filter(each => each.connectModel).length;
    }

    protected get headers(): BetterDataTableHeader[] {
      return [
        {text: this.$t('common.id'), align: 'start', sortable: true, value: 'id'},
        {text: this.$t('common.name'), align: 'start', value: 'name'},
        {text: this.$t('communities.fields.locationCity'), align: 'start', sortable: true, value: 'location_city'},
        {text: this.$t('common.onCommunities'), align: 'center', value: 'shareCta', sortable: false},
        {text: this.$t('common.assigned'), align: 'center', value: 'connectModel'},
      ];
    }

    get combinedSearch() {
      // we make sure search is triggered even when search input is empty. otherwise our custom filter is not applied
      // also A : B ensures that seach changes when the active/assigned switch changes
      // we also include city selections to trigger filter updates
      return (this.search||'').toString() +
        (this.onlyAssigned ? 'A' : 'B') +
        (this.onlyActive ? 'A' : 'B') +
        this.selectedCities.join('|');
    }

    protected get getServiceProvider(): IShortServiceProvider {
      const sp = this.serviceProviders.find(each => each.id == this.spId);
      return sp ? sp : ({} as IShortServiceProvider);
    }

    protected get cityOptions(): string[] {
      return Array.from(
        new Set(
          this.communityItems
            .filter((each: ICommunityWithConnect) => this.matchesBaseFilters(each))
            .map((each: ICommunityWithConnect) => (each.location_city || '').trim())
            .filter((each: string) => !!each)
        )
      ).sort((a: string, b: string) => a.localeCompare(b));
    }

    created() {
      this.subscribes = this.$ons({
        'login.OK': this.loadData,
        'backend.call.OK.communitiesLoaded': this.onCommunitiesLoaded,
      });
    }

    beforeDestroy() {
      this.$offs(this.subscribes);
    }

    private loadData(initial: boolean = false) {
      // load only if it's a reload (not initial), or if there are no communities
      if (!initial || !this.communities || !this.communities.length) {
        this.fetchAllCommunities();
      }
      else {
        this.onCommunitiesLoaded();
      }
      if (initial || !this.serviceProviders || !this.serviceProviders.length) {
        this.fetchServiceProviders();
      }
      this.fetchProviderCommunities();
    }

    private onCommunitiesLoaded() {
      this.cityByCommunityId = {};
      this.communities.forEach((each: IShortCommunity) => {
        this.cityByCommunityId[each.id] = each.location_city || '';
      });
      this.communityItems.forEach(each => {
        each.location_city = this.cityByCommunityId[each.id] || null;
      });
    }

    private async fetchProviderCommunities() {
      this.connectionsLoading = true;
      try {
        const items = await communityServiceProviderService.fetchByServiceProvider(this.spId);
        this.communityItems = items.map(each => ({
          id: each.id,
          name: each.name,
          active: each.active,
          connectModel: each.assigned,
          location_city: this.cityByCommunityId[each.id] || null,
        }));
      } finally {
        this.connectionsLoading = false;
      }
    }

    private onServiceProviderSelected(serviceProviderId: number) {
      if (this.$route.params.spId != `${serviceProviderId}`) {
        this.$router.push({name: 'serviceProvidersLockers', params: {spId: `${serviceProviderId}`}});
      }
    }

    private matchesBaseFilters(item: ICommunityWithConnect): boolean {
      const searchLower = (this.search || '').toString().toLowerCase();
      const notBySearch = searchLower.length &&
        !`${item.id}`.includes(searchLower) &&
        (item.name || '').toLowerCase().indexOf(searchLower) === -1;
      const notByActive = this.onlyActive && !item.active;
      const notByAssigned = this.onlyAssigned && !item.connectModel;
      return !notBySearch && !notByActive && !notByAssigned;
    }

    private communitiesFilter(
      value: any,
      search: string,
      item: ICommunityWithConnect
    ) {
      const notByCity = this.selectedCities.length && !this.selectedCities.includes(item.location_city || '');
      return this.matchesBaseFilters(item) && !notByCity;
    }

    private async onSwitch(communityId: number, connect: boolean) {
      this.connectionsLoading = true;
      try {
        if (connect) {
          await communityServiceProviderService.create(communityId, this.spId);
        }
        else {
          await communityServiceProviderService.destroy(communityId, this.spId);
        }
        await this.fetchProviderCommunities();
      } catch (_e) {
        this.$uiBus.emit('alert', 'KO');
      } finally {
        this.connectionsLoading = false;
      }
    }

  }
